import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { DOT_RGB_YELLOW, PlateCanvas } from 'classes/plate-canvas';
import { ErrorBoundary } from 'components/common/error-boundary';
import {
  DOT_SIZE_SM,
  MAX_SHOT_OPACITY,
  SHOT_OPACITY_DELTA,
} from 'enums/canvas';
import { SubStatus } from 'enums/installation';
import { isAppearanceDark } from 'index';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { IPlateLocExt } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { useEffect, useRef, useState } from 'react';

const COMPONENT_NAME = 'DataCollectorPlateView';

const REDRAW_DEQUEUE_MS = 100;

interface IProps {
  state: SubStatus;
  complete: boolean;
  shots: IMachineShot[];
}

export const DataCollectorPlateView = (props: IProps) => {
  const [canvasDef] = useState(PlateCanvas.makeLandscape(isAppearanceDark()));

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);
  const completeCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      console.debug(`${COMPONENT_NAME}: update imgSrc`);

      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        mainCanvas.current,
        shotsCanvas.current,
        completeCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawMain
  useEffect(() => {
    try {
      const canvas = mainCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      canvasDef.drawStrikeZoneAutoAdjustArea(ctx);
      canvasDef.drawStrikeZone(ctx);
      canvasDef.drawGround(ctx);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, []);

  // drawShots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = shotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (props.complete) {
          return;
        }

        // newest first
        const plates = props.shots
          .map((shot) => {
            const o: IPlateLocExt = {
              ...TrajHelper.getPlateLoc(shot.traj),
              _created: shot._created,
              _id: shot._id,
              aiming: shot.aiming,
            };

            return o;
          })
          .sort((a, b) => -a._created.localeCompare(b._created));

        for (const p of plates) {
          const i = plates.findIndex((m) => m === p);
          const opacity = Math.max(
            0,
            MAX_SHOT_OPACITY - i * SHOT_OPACITY_DELTA
          );

          await canvasDef.drawDot(ctx, {
            plate: p,
            labelText:
              i === 0
                ? // only label the latest shot's location
                  `${p.plate_x.toFixed(1)}', ${p.plate_z.toFixed(1)}'`
                : undefined,
            size: DOT_SIZE_SM,
            color: `rgba(${DOT_RGB_YELLOW}, ${opacity})`,
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [props.shots, props.complete]);

  // drawComplete
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = completeCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!props.complete) {
          return;
        }

        switch (props.state) {
          case SubStatus.Error: {
            await canvasDef.drawCrossmark(ctx);
            break;
          }

          case SubStatus.Completed: {
            await canvasDef.drawCheckmark(ctx);
            break;
          }

          default: {
            break;
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [props.state, props.complete]);

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <div
        style={{
          height: '300px',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          className="slider"
          style={{
            aspectRatio: '1.6',
            width: 'auto',
            // ensures this is centered horizontally
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translate(-50%, 0)',
          }}
        >
          <div
            className="sizer"
            style={{
              aspectRatio: '1.6',
              position: 'absolute',
              height: '100%',
              width: '100%',
            }}
          >
            {/* for log rocket recordings to show the results */}
            {imgSrc && <img className="image-wrapper" src={imgSrc} />}
            <canvas
              ref={mergeCanvas}
              data-identifier="merge-canvas"
              width={canvasDef.CONFIG.canvas.width_px}
              height={canvasDef.CONFIG.canvas.height_px}
            />
            <canvas
              ref={mainCanvas}
              data-identifier="main-canvas"
              width={canvasDef.CONFIG.canvas.width_px}
              height={canvasDef.CONFIG.canvas.height_px}
            />
            <canvas
              ref={shotsCanvas}
              data-identifier="shots-canvas"
              width={canvasDef.CONFIG.canvas.width_px}
              height={canvasDef.CONFIG.canvas.height_px}
            />
            <canvas
              ref={completeCanvas}
              data-identifier="complete-canvas"
              width={canvasDef.CONFIG.canvas.width_px}
              height={canvasDef.CONFIG.canvas.height_px}
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
